import _ from 'lodash';
import log from '$$log';
import defaultConfig from './defaultConfig';
import workloadLayout from '../workload/layout';
import { updateStartMondayBack } from '../helpers/calendarHelpers';
import templates from './templates';
import ProjectCalendar from '../controllers/projectCalendar';
import dateHelper from '$$dateFormatsHelper';
import constants from '$$helpers/constants';
import rights from '../../../components/rights';
import pricingHelper from '$$helpers/pricingHelper';

class Config {
  constructor() {
    this.instance = null;
    this.projectConfig = null;
    this.projectId = null;
  }

  init(projectId, instance, projectConfig) {
    this.instance = instance;
    this.projectId = projectId;
    this.projectConfig = projectConfig;

    this.initPlugins(); // enable built-in plugins
    this.autoScheduling(this.projectConfig.auto_scheduling);

    const tCalendar = log.time('ganttConfigure.config.calendar');

    this.projectConfig.start_monday = user.settings.start_monday;
    this.instance.config.drag_move = true;
    this.instance.config.userResourceId = this.instance.ganttDataApi.getResourceByUserId(user.id)?.id;
    if (Array.isArray(projectId)) {
      projectId.forEach(id => this.initProjectCalendar(id));
    } else {
      this.initProjectCalendar(projectId);
    }
    tCalendar.end();

    const tBase = log.time('ganttConfigure.config.base');

    this.base();
    this.initBaseLayout();
    tBase.end();

    const tOptions = log.time('ganttConfigure.config.options');

    this.showDrag();
    this.staticBackground();
    this.durationBase();
    this.durationViewMode();
    this.durationView();
    this.durationData();
    this.skipOffTime();
    // this.showMarkers();
    this.criticalPath();
    this.floatTime();
    this.expandButton();
    // this.inlineEditors(instance);
    // this.initResourceLoadingType(projectConfig.resource_loading_type);
    this.startOnMonday(projectConfig.start_monday);
    this.setReadonlyMode();
    tOptions.end();

    const tTemplates = log.time('ganttConfigure.config.templates');

    this.initGanttTemplates();
    this.initGanttLocales();
    this.checkDisableClass();
    tTemplates.end();
  }

  base() {
    // own configuration
    this.instance.config.show_advanced_buttons = _.get(this.projectConfig, 'show_advanced_buttons', false);
    this.instance.config.show_resource_avatar = _.get(this.projectConfig, 'show_resource_avatar', false);
    this.instance.config.right_side_text = this.projectConfig.right_side_text || 'insideText';
    this.instance.config.current_day_marker = _.get(this.projectConfig, 'current_day_marker', true);
    this.instance.config.highlight_overdue = !pricingHelper.checkPricingAccess('overdue_tasks') ? false : this.projectConfig.highlight_overdue * 1 || false;
    this.instance.config.discoloration_tasks = this.projectConfig.discoloration_tasks || defaultConfig.discolorationTasks;
    this.instance.config.cross_out_tasks = this.projectConfig.cross_out_tasks || defaultConfig.crossOutTasks;

    this.instance.config.highlight_critical_path = this.projectConfig.highlight_critical_path * 1 || false;
    this.instance.config.show_float_time = this.projectConfig.show_float_time * 1 || false;
    this.instance.config.date_format = defaultConfig.dateFormat;
    this.instance.config.touch = defaultConfig.touchEnabel;
    this.instance.config.keep_grid_width = defaultConfig.stableGridWidth;
    this.instance.config.round_dnd_dates = defaultConfig.roundDndDates;
    this.instance.config.details_on_create = false;
    this.instance.config.smart_rendering = true;
    this.instance.config.static_background_cells = true;
    this.instance.config.smart_scales = true;
    this.instance.config.scroll_on_click = true;
    this.instance.config.show_unscheduled = true;
    this.instance.config.cascade_delete = false;
    this.instance.ignore_time = null;
    this.instance.config.autoscroll = true;
    this.instance.config.autoscroll_speed = 100;
    this.instance.config.min_grid_column_width = 50;
    this.instance.config.autofit = false;
    this.instance.config.link_arrow_size = 5;
    this.instance.config.link_radius = 1;
    this.instance.config.drag_project = true;
    this.instance.config.initial_scroll = false;
    this.instance.config.scroll_size = 15;
    if (this.instance.ganttDataApi.isBase()) {
      this.instance.config.reorder_grid_columns = true;
      this.instance.config.show_grid = this.instance.ganttDataApi.getShowGrid();
    }
    this.instance.config.isJira = this.projectConfig.isJira;
    this.instance.config.progress = this.projectConfig.progress;
    this.instance.config.date_grid = dateHelper.getDateFormatForGanttGrid();
    this.instance.config.auto_types = false;
    this.instance.config.types.button = 'button';
    this.instance.config.resource_property = 'assign';// need for switch of plugin from gantt for assignment, default value 'owner_id' we are already use
    this.instance.config.show_days_on_chart = _.get(this.projectConfig, 'show_days_on_chart', false);
  }

  initPlugins() {
    this.instance.plugins({
      auto_scheduling: true,
      critical_path: true,
      marker: true,
      drag_timeline: true,
    });
  }

  initBaseLayout() {
    this.instance.config.layout = {
      css: 'gantt_container',
      rows: [{
        id: 'ganttChart',
        gravity: 3,
        cols: [{
          view: 'grid',
          group: 'grids',
          scrollY: 'scrollVer',
        },
        {
          resizer: true,
          width: 1,
          group: 'vertical',
        },
        {
          view: 'timeline',
          scrollX: 'scrollHor',
          scrollY: 'scrollVer',
        },
        {
          view: 'scrollbar',
          id: 'scrollVer',
          group: 'vertical',
        },
        ],
      }],
    };

    if (!this.instance.ganttDataApi.isLink() && !this.instance.ganttDataApi.isHistory()) {
      this.instance.config.layout.rows = [
        ...this.instance.config.layout.rows,
        {
          resizer: true, id: 'workloadResizer', width: 1, next: 'workload', css: 'workload-resizer',
        },
        workloadLayout.getLayout(this.instance.ganttDataApi.isBase(), this.instance.ganttDataApi.isExport()),
      ];
    }
    this.instance.config.layout.rows.push({ view: 'scrollbar', id: 'scrollHor', group: 'horizontal' });
  }

  updateProjectConfig(projectId, projectConfig) {
    const projectIds = Array.isArray(this.projectId) ? this.projectId : [this.projectId];
    const isTargetProject = projectIds.some(id => +id === +projectId);

    if (!isTargetProject) {
      return;
    }

    if (this.instance.config.multiview) {
      ProjectCalendar.addOrUpdateCalendar(projectId, projectConfig.durationData);

      return;
    }

    this.autoScheduling(projectConfig.auto_scheduling);
    this.durationView(projectConfig.duration_view);
    this.durationData(projectConfig.durationData);
    this.instance.config.auto_budget = projectConfig.auto_budget;
    this.instance.config.resource_loading_type = projectConfig.resource_loading_type;
    this.instance.config.progress = projectConfig.progress;
    this.instance.config.estimation_mode = projectConfig.estimation_mode;
    this.instance.config.apply_resource_calendar = projectConfig.apply_resource_calendar;
  }

  durationViewMode(value = this.projectConfig.view_mode || 'auto') {
    const viewModeStrategyRow = {
      auto: 30,
      compact: 24,
      touch: 44,
    };

    const viewModeStrategyTask = {
      auto: 22,
      compact: 16,
      touch: 36,
    };

    if (this.instance.config.baseline > 0) {
      this.instance.config.row_height = viewModeStrategyRow[value] * 2;
    } else {
      this.instance.config.row_height = viewModeStrategyRow[value];
    }

    this.instance.config.original_row_height = viewModeStrategyRow[value];
    this.instance.config.bar_height = viewModeStrategyTask[value];
    this.instance.config.view_mode = value;

    const ganttContainer = document.querySelector('.dhx_gantt_container');

    if (ganttContainer) {
      _.each(Object.keys(viewModeStrategyRow), item => {
        ganttContainer.classList.remove(item);
      });

      ganttContainer.classList.add(value);
    }
  }

  criticalPath(value = this.projectConfig.highlight_critical_path) {
    this.instance.config.highlight_critical_path = (this.instance.ganttDataApi.isBase() || this.instance.ganttDataApi.isExport() || this.instance.ganttDataApi.isLink() || this.instance.ganttDataApi.isHistory())
      ? value : false;
  }

  floatTime(value = this.projectConfig.show_float_time) {
    this.instance.config.show_float_time = (this.instance.ganttDataApi.isBase() || this.instance.ganttDataApi.isExport() || this.instance.ganttDataApi.isHistory())
      ? value : false;
  }

  initProjectCalendar(projectId) {
    this.instance.config.correct_work_time = false;
    this.instance.config.work_time = true;

    ProjectCalendar.addOrUpdateCalendar(projectId);
  }

  showDrag() {
    this.instance.config.show_drag_vertical = true;
    this.instance.config.drag_label_width = 150;
    // this.instance.config.dnd_sensitivity = 2;
    // this.instance.config.show_drag_dates = true;
  }

  staticBackground() {
    this.instance.config.static_background = true;
  }

  durationBase() {
    this.instance.config.duration_unit = 'minute';
    this.instance.config.duration_step = 1;
    this.instance.config.min_duration = this.instance.config.duration_step * 60 * 1000;

    this.instance.config.time_step = 0;
    this.instance.config.scale_offset_minimal = true;
  }

  durationView(value = this.projectConfig.duration_view) {
    this.instance.config.duration_view = value;
  }

  durationData(value = this.projectConfig.durationData) {
    this.instance.config.durationData = { ...value };
  }

  autoScheduling(value) {
    this.instance.config.auto_scheduling = {
      enabled: value,
      gap_behavior: 'compress',
      descendant_links: true,
    };
    this.instance.config.auto_scheduling_initial = value;

    // TODO needs to check "true"
    this.instance.config.auto_scheduling_strict = false;
    this.instance.config.auto_scheduling_compatibility = false;
  }

  showMarkers() {
    this.instance.config.show_markers = true;
  }

  skipOffTime() {
    this.instance.config.skip_off_time = this.projectConfig.skip_off_time || false;
  }

  initGanttTemplates() {
    this.instance.templates.grid_row_class = templates.gridRowClass(this.instance);
    this.instance.templates.task_row_class = templates.taskRowClass(this.instance);
    this.instance.templates.task_class = templates.taskClass(this.instance);
    this.instance.templates.leftside_text = templates.leftSideText(this.instance);
    this.instance.templates.rightside_text = templates.rightSideText(this.instance);
    this.instance.templates.task_text = templates.taskText(this.instance);
    this.instance.templates.grid_open = templates.gridOpen(this.instance);
    this.instance.templates.timeline_cell_class = templates.timelineCellClass(this.instance);
    this.instance.templates.link_class = templates.linkClass(this.instance);
    this.instance.templates.drag_date = templates.dragDate(this.instance);
  }

  expandButton() {
    this.instance.config.expand_button_y = 35;
    this.instance.config.expand_button_x = 45;
  }

  initGanttLocales() {
    this.instance.locale = { date: {}, labels: { link_start: __('link_start'), link_end: __('link_end') } };
    // // get locales from webix locale files and convert them to DHTMLX format
    // [
    //   { webix: 'monthFull', dhtmlx: 'month_full' },
    //   { webix: 'monthShort', dhtmlx: 'month_short' },
    //   { webix: 'dayFull', dhtmlx: 'day_full' },
    //   { webix: 'dayShort', dhtmlx: 'day_short' },
    // ].forEach(val => {
    //   this.instance.locale.date[val.dhtmlx] = webix.i18n.locales[GT.locales.supported[user.locale].code].calendar[val.webix].map(string => string.charAt(0).toUpperCase() + string.slice(1));
    // });

    // from "Styczeń, Luty, Marzec, Kwiecień, Maj, Czerwiec, Lipiec, Sierpień, Wrzesień, Październik, Listopad, Grudzień" to [Styczeń, Luty, Marzec, Kwiecień, Maj, Czerwiec, Lipiec, Sierpień, Wrzesień, Październik, Listopad, Grudzień]
    this.instance.locale.date.day_full = __('calendar_days_full').split(',').map(string => string.trim());
    this.instance.locale.date.day_short = __('calendar_days_short').split(',').map(string => string.trim());
    this.instance.locale.date.month_full = __('calendar_months_full').split(',').map(string => string.trim());
    this.instance.locale.date.month_short = __('calendar_months_short').split(',').map(string => string.trim());

    this.instance.locale.labels.type_task = __('gantt_task');
    this.instance.locale.labels.type_project = __('gantt_project');
    this.instance.locale.labels.type_milestone = __('gantt_milestone');
    this.instance.locale.labels.section_assignTo = __('gantt_assign_to');
    this.instance.locale.labels.link = __('gantt_link');
    this.instance.locale.labels.confirm_link_deleting = __('gantt_confirm_link_deleting');
  }

  // initResourceLoadingType(value) {
  //   this.instance.config.resource_loading_type = value || 'hours';
  // }

  startOnMonday(value) {
    if (!this.instance.ganttDataApi.isBase()) {
      this.instance.config.start_on_monday = +value;
      webix.Date.startOnMonday = this.instance.config.start_on_monday || +user.settings.start_monday;

      return;
    }

    if (user.settings?.start_monday !== null) {
      this.instance.config.start_on_monday = +user.settings.start_monday;
    } else {
      this.instance.config.start_on_monday = +value;
    }
    updateStartMondayBack(this.instance, this.instance.config.start_on_monday); // TODO check it
    webix.Date.startOnMonday = this.instance.config.start_on_monday;
  }

  setReadonlyMode() {
    this.instance.config.readonly = !!(!this.instance.ganttDataApi.isBase() || this.projectConfig.isGlobal || this.instance.config.isArchived);
  }

  setSkin(userSkin = this.projectConfig.userSkin) {
    this.removeSkinClass();
    const skin = !constants.SKINS[userSkin] ? 4 : userSkin;

    this.instance.$container.classList.add(constants.SKINS[skin].class);
    if (this.instance.config.multiview) {
      this.instance.$container.classList.add('isMultiview');
    } else if (!this.instance.config.multiview && this.instance.$container.classList.contains('isMultiview')) {
      this.instance.$container.classList.remove('isMultiview');
    }
  }

  removeSkinClass() {
    const classList = this.instance.$container.classList;
    const values = Object.values(constants.SKINS);

    values.forEach(value => {
      if (classList.contains(value.class)) {
        this.instance.$container.classList.remove(value.class);
      }
    });
  }

  setConfigParameter(parameterName, value, needRender = false) {
    this.instance.config[parameterName] = value;
    if (needRender) {
      const t = log.time('ganttRender.instance');

      this.instance.render();
      t.end();
      if (parameterName === 'show_advanced_buttons') this.drawHorizontalLine();
    }
  }

  checkDisableClass() {
    const ganttContainer = document.querySelector('.dhx_gantt_container');

    if (this.instance.config.isArchived) {
      !ganttContainer.classList.contains('disable') && ganttContainer.classList.add('disable');
      !ganttContainer.classList.contains('archived') && ganttContainer.classList.add('archived');
    } else {
      ganttContainer.classList.contains('disable') && ganttContainer.classList.remove('disable');
      ganttContainer.classList.contains('archived') && ganttContainer.classList.remove('archived');
    }
  }

  drawHorizontalLine() {
    this.instance.$container.classList.remove('no-buttons');
    if (!this.instance.ganttDataApi.isBase() || this.projectConfig.isGlobal || !rights.project.hasRight(this.projectId, 'all_tasks')
      || !rights.project.hasRight(this.projectId, 'create_task') || !this.instance.config.show_advanced_buttons) {
      this.instance.$container.classList.add('no-buttons');
    }
  }
}

export default new Config();
